import { useEffect, useState, useRef } from 'react';
import {
  TabsGroup,
  TabsWrapper,
  HeaderSection,
} from 'cdk-radial';
import { TabPanel, customScrollbar, PageHeaderNavigationTab } from 'cdk-radial';
import styled from 'styled-components';
import useProfileStore from "../core/UserProfileStore";
import {AccountingDashboard} from "./AccountingDashboard";
import {CADashboard} from "./CADashboard";
import {VISDashboard} from "./VISDashboard";
import {USDashboard} from "./USDashboard";
import { MultiSelectDropdownMenu } from '../common/MultiSelectDropdownMenu';
import { IDashboardAccessSetting } from './useDashboardQueries';
import useUserSettingStore from './UserSettingStore';

interface TabItem {
  id: string;
  name: string;
  panelId: string;
  renderPanel: any;
  renderTab: any;
}

export const StyledContentContainer = styled.div`
  ${customScrollbar};
  background-color: ${({ theme }) => theme.color.additional.light.value};
  color: ${({ theme }) => theme.color.additional.dark.value};
`;

const ALL_TABS: TabItem[] = [
  {
    id: 'accounting',
    name: 'Accounting',
    panelId: 'accounting-1',
    renderPanel: (props: any) => (
      <TabPanel {...props}>
        <AccountingDashboard />
      </TabPanel>
    ),
    renderTab: (props: any) => (
      <PageHeaderNavigationTab label="Accounting" {...props} tabIndex={0} />
    ),
  },
  {
    id: 'common-admin',
    name: 'Common Admin',
    panelId: 'common-admin-2',
    renderPanel: (props: any) => (
      <TabPanel {...props}>
        <CADashboard />
      </TabPanel>
    ),
    renderTab: (props: any) => (
      <PageHeaderNavigationTab label="Common Admin" {...props} tabIndex={1} />
    ),
  },
  {
    id: 'unit-stocking',
    name: 'Unit Stocking',
    panelId: 'unit-stocking-3',
    renderPanel: (props: any) => (
      <TabPanel {...props}>
        <USDashboard />
      </TabPanel>
    ),
    renderTab: (props: any) => (
      <PageHeaderNavigationTab label="Unit Stocking" {...props} tabIndex={2} />
    ),
  },
  {
    id: 'vis',
    name: "Vehicle Inventory Suite",
    panelId: 'vis-4',
    renderPanel: (props: any) => (
      <TabPanel {...props}>
        <VISDashboard></VISDashboard>
      </TabPanel>
    ),
    renderTab: (props: any) => (
      <PageHeaderNavigationTab label="Vehicle Inventory Suite" {...props} tabIndex={3}/>
    ),
  }
];

export const DomainTabs = () => {
  const [tabItems, setTabItems] = useState<TabItem[]>([]);
  const itemsRef = useRef([]) as any;
  const tabsWrapperRef = useRef();
  const [activeNavigationItemIndex, setActiveNavigationItemIndex] = useState(0);
  const { roles } = useProfileStore();
  const {dashboardAccessSetting} = useUserSettingStore();
  const [domainOptions, setDomainOptions] = useState([]) as any;
  const [selectDomain, setSelectDomain] = useState([]) as any;

  // render tabs
  const renderTabs = () => {
    return tabItems.map((item: any, index: any) => {
      const tabRef = (element: any) => (itemsRef.current[index] = element);
      const isActive = activeNavigationItemIndex === index;
      const handleClick = (event: any) => {
        setActiveNavigationItemIndex(index);
        if (item.onClick) {
          item.onClick(event);
        }
      };
      const handleFocus = (event: any) => {
        if (item.onFocus) {
          item.onFocus(event);
        }
      };
      return item.renderTab({
        'aria-controls': item.panelId,
        'aria-selected': isActive,
        id: item.id,
        isActive,
        dataTestId:item.panelId,
        key: item.id,
        onClick: handleClick,
        onFocus: handleFocus,
        ref: tabRef,
        role: 'tab',
        tabIndex: isActive ? 0 : -1,
      });
    });
  };

  const handleDomainSelection = (selectedDomain: any) => {
    setSelectDomain(selectedDomain);
    const tab = selectedDomain.map((domain:any) => domain.obj);
    setTabItems(tab);
    setActiveNavigationItemIndex(0);
  };

  useEffect(() => {

    const accessibleTabs = ALL_TABS.filter((tab) => {
      const setting = dashboardAccessSetting
        ?.filter((domain:any) => domain.enabled)
        ?.find(
        (s: IDashboardAccessSetting) => s.dashboardId === tab.id
      );
      if (!setting) return false;
      return setting.roles.filter(Boolean).some((r: string) =>
        roles.filter(Boolean).some((userRole: string) =>
          r.toLowerCase().includes(userRole) || userRole.includes(r.toLowerCase())
        )
      );
    });

    const options = accessibleTabs.map((tab) => ({
      value: tab.id,
      label: tab.name,
      obj: tab,
    }));

    setDomainOptions(options);
    setSelectDomain(options);
    setTabItems(accessibleTabs);
  }, [dashboardAccessSetting, roles]);

  return (
    <>
      <HeaderSection>

        {
        domainOptions?.length > 1 &&
          <div style={{width:'300px', marginLeft:'10px',marginBottom:'10px'}}>
            <MultiSelectDropdownMenu
              options={domainOptions || []}
              name="domain"
              onSelect={handleDomainSelection}
              value={selectDomain}
              label={'Select Domain'}
              placeholder={'Domain'}
            />
          </div>
        }

        <TabsWrapper>
          <TabsGroup
            label={'Label that describes the purpose of the set of tabs'}
            ref={(element: any) => (tabsWrapperRef.current = element)}
            role="tablist"
          >
            {renderTabs()}
          </TabsGroup>
        </TabsWrapper>
      </HeaderSection>
      <StyledContentContainer>
        {tabItems &&
          tabItems.length > 0 &&
          tabItems[activeNavigationItemIndex]?.renderPanel({
            'aria-labelledby': tabItems[activeNavigationItemIndex].id,
            id: tabItems[activeNavigationItemIndex].panelId,
            role: 'tabpanel',
          })}
      </StyledContentContainer>
    </>
  );
};
